// @flow
import {useState} from 'react';
import type {Node} from 'react';
import {useNavigate} from 'react-router-dom';
import {useQuery} from '@tanstack/react-query';
import {Box, Typography} from '@wellstone-solutions/web';
import {Api} from '@wellstone-solutions/common';
import {useTheme} from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  RowCount,
  PaginatedListGrid,
  usePaginationParams,
} from 'modules/datagrid';
import {useStores} from 'hooks/useStores';
import {memberListColumns} from './columns';
import {ResendInvitationModal} from './ResendInvitationModal';
import {UnassignedEmptyState} from 'components/UnassignedEmptyState';

const ROLE_PATIENT = 'patient';

export const PublicInvitesTab = (): Node => {
  const {meStore} = useStores();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const [memberToInvite, setMemberToInvite] = useState(null);

  const {page, setPage, pageSize, setPageSize, ordering, setOrdering} =
    usePaginationParams({
      defaultSortModel: [{field: 'member__name', sort: 'asc'}],
    });

  // Members in "public" groups (their group's category is marked public). Interim
  // raw v2 call (snake_case params sent as-is) to avoid regenerating the shared
  // typed client. Swappable for `membersList` later without UI changes.
  const query = useQuery({
    queryKey: ['members', 'publicInvites', {page, pageSize, ordering}],
    queryFn: async () => {
      const response = await Api.Instance.v2().get('/members/', {
        params: {
          public_groups: true,
          role: ROLE_PATIENT,
          page,
          page_size: pageSize,
          ordering,
        },
      });
      return response.data;
    },
  });

  const rows =
    query.data?.results?.map((m) => ({
      id: m.id,
      member: m,
    })) ?? [];

  const columns = isMobile
    ? [memberListColumns.mobile]
    : [
        memberListColumns.name,
        memberListColumns.lastAppOpen,
        memberListColumns.dateProfileCreated,
      ];

  if (meStore.ehrIntegrationOption && !isMobile) {
    columns.push({
      ...memberListColumns.ehr,
      headerName:
        meStore.ehrIntegrationOption?.name ?? memberListColumns.ehr.headerName,
    });
  }

  columns.push(
    memberListColumns.getRowActions({onResendInvite: setMemberToInvite}),
  );

  const handleRowClick = ({row}) => {
    if (globalThis.getSelection()?.type === 'Range') {
      return;
    }
    navigate(`/members/${row.member.id}`);
  };

  if (query.isError) {
    return (
      <Box sx={styles.message}>
        <Typography>
          We couldn&rsquo;t load public invites. Please try again.
        </Typography>
      </Box>
    );
  }

  if (!query.isFetching && (query.data?.count ?? 0) === 0) {
    return <UnassignedEmptyState title="No public invites yet" />;
  }

  return (
    <Box>
      <RowCount
        isLoading={query.isFetching}
        label="public invite"
        count={query.data?.count || 0}
      />

      <PaginatedListGrid
        testId="public-invites-datagrid"
        columns={columns}
        rows={rows}
        rowCount={query.data?.count || 0}
        isLoading={query.isFetching}
        page={page}
        pageSize={pageSize}
        ordering={ordering}
        isMobile={isMobile}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
        onSortModelChange={setOrdering}
        onRowClick={handleRowClick}
      />

      <ResendInvitationModal
        member={memberToInvite}
        open={!!memberToInvite}
        onComplete={() => {
          setMemberToInvite(null);
        }}
      />
    </Box>
  );
};

const styles = {
  message: {
    p: 4,
    textAlign: 'center',
  },
};
